import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PerfectPour from "@/game/minigames/TimingBar";
import MemoryMatch from "@/game/minigames/MemoryMatch";
import { useTr } from "@/autoTranslate";

/**
 * Inline mini-game section — embedded into the portfolio between content sections.
 * Users click "Play" to focus it (keyboard listeners activate then).
 */
export default function InlineMinigame({
  id,
  chapter,
  title,
  intro,
  kind, // "pour" | "match"
  config,
  bg = "var(--paper)",
}) {
  const tr = useTr();
  const [playing, setPlaying] = useState(false);
  const [key, setKey] = useState(0);
  const sectionRef = useRef(null);

  // Auto-pause when user scrolls this section out of view
  useEffect(() => {
    if (!playing) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) setPlaying(false);
        }
      },
      { threshold: 0.35 },
    );
    if (sectionRef.current) io.observe(sectionRef.current);
    return () => io.disconnect();
  }, [playing]);

  return (
    <section
      id={id}
      ref={sectionRef}
      data-testid={`inline-minigame-${id}`}
      data-minigame-active={playing ? "true" : "false"}
      className="relative px-6 md:px-10 py-8 md:py-12"
      style={{ background: bg }}
    >
      <div className="pb-4 flex items-center justify-between font-mono-label text-[11px] uppercase tracking-widest opacity-80">
        <span>{tr(chapter)}</span>
        <span>{playing ? tr("Playing — Esc to exit") : tr("Interlude · click to play")}</span>
      </div>

      <div className="mt-8 md:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="display-sans text-5xl md:text-[6vw] leading-[0.9]"
          >
            {tr(title)}
          </motion.h2>
          <p className="mt-5 text-base md:text-lg max-w-md opacity-85">{tr(intro)}</p>
          {!playing && (
            <button
              onClick={() => {
                setKey((k) => k + 1);
                setPlaying(true);
                // scroll into view so game panel is clearly visible
                setTimeout(() => {
                  sectionRef.current?.scrollIntoView({
                    block: "center",
                    behavior: "smooth",
                  });
                }, 100);
              }}
              data-testid={`minigame-play-${id}`}
              className="btn-pill warm mt-8"
            >
              {tr("Play →")}
            </button>
          )}
          {playing && (
            <button
              onClick={() => setPlaying(false)}
              data-testid={`minigame-exit-${id}`}
              className="btn-pill ghost mt-8"
            >
              {tr("Exit ✕")}
            </button>
          )}
        </div>

        <div className="lg:col-span-7">
          <div className="panel p-6 md:p-8 relative min-h-[380px]">
            <AnimatePresence mode="wait">
              {playing ? (
                <motion.div
                  key={`play-${key}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  {kind === "pour" && (
                    <PerfectPour
                      config={config}
                      onDone={() => setPlaying(false)}
                      onClose={() => setPlaying(false)}
                    />
                  )}
                  {kind === "match" && (
                    <MemoryMatch
                      config={config}
                      onDone={() => setPlaying(false)}
                      onClose={() => setPlaying(false)}
                    />
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="idle"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center min-h-[340px] text-center"
                >
                  <div className="display-serif italic text-5xl md:text-6xl opacity-25">
                    ~ {kind === "pour" ? "coffee" : "memory"} ~
                  </div>
                  <div className="mt-4 font-mono-label text-[11px] uppercase tracking-widest opacity-60">
                    {tr("press Play to focus this mini-game")}
                  </div>
                  <div className="mt-6 flex flex-wrap gap-2 justify-center">
                    {kind === "pour" && (
                      <>
                        <span className="sticker">3 rounds</span>
                        <span className="sticker">timing</span>
                        <span className="sticker">~90 sec</span>
                      </>
                    )}
                    {kind === "match" && (
                      <>
                        <span className="sticker">memory</span>
                        <span className="sticker">star rating</span>
                        <span className="sticker">~90 sec</span>
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
